import { useState, useEffect, useCallback, useRef } from 'react';
import { Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export function useTemplatesCoopers(userId, getAuthHeader) {
  const [templates, setTemplates] = useState([]);
  const [variablesDisponibles, setVariablesDisponibles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // État pour le dialogue d'édition
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    type_document: 'proposition',
    contenu_html: '',
    description: ''
  });
  
  // État pour gérer les types personnalisés
  const [isCustomType, setIsCustomType] = useState(false);
  const [customTypeValue, setCustomTypeValue] = useState('');
  
  // État pour la prévisualisation
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [variablesExpanded, setVariablesExpanded] = useState({});
  
  // État pour le partage
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareTemplate, setShareTemplate] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  
  // Référence pour l'éditeur
  const editorRef = useRef(null);
  const fetchingRef = useRef(false);

  // Fonctions pour l'éditeur HTML personnalisé
  const insertVariable = (variable) => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(variable));
        range.collapse(false);
      } else {
        editorRef.current.innerHTML += variable;
      }
      setFormData({ ...formData, contenu_html: editorRef.current.innerHTML });
    }
  };

  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setFormData({ ...formData, contenu_html: editorRef.current.innerHTML });
    }
  };

  const insertHeading = (level) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const heading = document.createElement(`h${level}`);
      heading.textContent = selection.toString() || `Titre niveau ${level}`;
      range.deleteContents();
      range.insertNode(heading);
      setFormData({ ...formData, contenu_html: editorRef.current.innerHTML });
    }
  };

  // Fonction pour générer et télécharger le document HTML
  const handleGenerateDocument = () => {
    const htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${formData.nom}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      max-width: 800px;
      margin: 40px auto;
      padding: 20px;
      color: #333;
    }
    h1, h2, h3 { color: #0a3d62; }
    table { border-collapse: collapse; width: 100%; margin: 20px 0; }
    td, th { border: 1px solid #ddd; padding: 12px; }
    th { background-color: #f8f9fa; }
  </style>
</head>
<body>
  ${formData.contenu_html}
</body>
</html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.nom.replace(/\s+/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    setSuccess('Document HTML généré et téléchargé avec succès!');
  };

  // Charger les templates et variables
  const fetchTemplates = useCallback(async () => {
    if (!userId || fetchingRef.current) return;
    
    fetchingRef.current = true;
    
    try {
      setLoading(true);
      setError(null);

      // Charger les templates avec axios
      const templatesRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/coopers/templates?cooper_id=${userId}`,
        { headers: getAuthHeader() }
      );
      setTemplates(templatesRes.data);

      // Charger les variables disponibles avec axios
      const variablesRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/coopers/templates/variables`,
        { headers: getAuthHeader() }
      );
      setVariablesDisponibles(variablesRes.data.variables || {});

    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [userId, getAuthHeader]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Créer ou mettre à jour un template
  const handleSaveTemplate = async () => {
    try {
      setError(null);
      setSuccess(null);

      // Préparer les données avec le bon type_document
      const dataToSave = {
        ...formData,
        type_document: isCustomType ? customTypeValue.trim() : formData.type_document
      };

      // Validation
      if (!dataToSave.nom.trim()) {
        setError('Le nom du template est requis');
        return;
      }
      if (!dataToSave.type_document || dataToSave.type_document === 'custom') {
        setError('Veuillez sélectionner ou saisir un type de document');
        return;
      }

      const url = editingTemplate
        ? `${process.env.REACT_APP_BACKEND_URL}/api/coopers/templates/${editingTemplate.id}?cooper_id=${userId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/coopers/templates?cooper_id=${userId}`;

      if (editingTemplate) {
        await axios.put(url, dataToSave, { headers: getAuthHeader() });
      } else {
        await axios.post(url, dataToSave, { headers: getAuthHeader() });
      }

      setSuccess(editingTemplate ? 'Template mis à jour avec succès !' : 'Template créé avec succès !');
      setIsEditing(false);
      setEditingTemplate(null);
      setIsCustomType(false);
      setCustomTypeValue('');
      setFormData({ nom: '', type_document: 'proposition', contenu_html: '', description: '' });
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  // Supprimer un template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/coopers/templates/${templateId}?cooper_id=${userId}`,
        { headers: getAuthHeader() }
      );

      setSuccess('Template supprimé avec succès !');
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  // Dupliquer un template
  const handleDuplicateTemplate = async (templateId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/coopers/templates/${templateId}/duplicate?cooper_id=${userId}`,
        {},
        { headers: getAuthHeader() }
      );

      setSuccess('Template dupliqué avec succès !');
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  // Charger un template par défaut
  const handleLoadDefaultTemplate = async (type) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/coopers/templates/default/${type}?cooper_id=${userId}`,
        { headers: getAuthHeader() }
      );

      const defaultTemplate = response.data;
      setFormData({
        nom: defaultTemplate.nom,
        type_document: defaultTemplate.type_document,
        contenu_html: defaultTemplate.contenu_html,
        description: defaultTemplate.description
      });
      setIsEditing(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  // Basculer l'état actif d'un template
  const toggleTemplateActive = async (template) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/coopers/templates/${template.id}?cooper_id=${userId}`,
        { is_active: !template.is_active },
        { headers: getAuthHeader() }
      );

      setSuccess('Statut du template mis à jour !');
      toast.success(`Template ${template.is_active ? 'désactivé' : 'activé'} avec succès`);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Prévisualiser un template
  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  // Générer le HTML complet pour impression/PDF
  const generateFullHTML = (template) => {
    return `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${template.nom} - COOPER'S Template</title>
  <style>
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
    h1, h2, h3 { color: #1e40af; }
    .header { border-bottom: 2px solid #1e40af; padding-bottom: 20px; margin-bottom: 30px; }
    .header h1 { margin: 0; font-size: 24px; }
    .header p { margin: 5px 0 0; color: #666; }
    .content { margin-top: 20px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
    @media print { body { margin: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>${template.nom}</h1>
    <p>Type: ${template.type_document} | ${template.is_active ? 'Actif' : 'Inactif'}</p>
  </div>
  <div class="content">
    ${template.contenu_html || '<p>Aucun contenu</p>'}
  </div>
  <div class="footer">
    <p>Document généré le ${new Date().toLocaleDateString('fr-FR')} - COOPER'S O'SCOP</p>
  </div>
</body>
</html>`;
  };

  // Imprimer un template
  const handlePrintTemplate = (template) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(generateFullHTML(template));
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 500);
      toast.success('Impression lancée');
    } else {
      toast.error('Impossible d\'ouvrir la fenêtre d\'impression');
    }
  };

  // Télécharger en PDF (via HTML)
  const handleDownloadPDF = async (template) => {
    try {
      // Create a hidden iframe for PDF generation
      const htmlContent = generateFullHTML(template);
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Download as HTML (user can convert to PDF or print to PDF)
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template.nom.replace(/[^a-z0-9]/gi, '_')}_template.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Template téléchargé (utilisez Imprimer > Enregistrer en PDF)');
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Ouvrir le dialogue de partage
  const handleShareTemplate = (template) => {
    setShareTemplate(template);
    setShareEmail('');
    setShareMessage(`Bonjour,\n\nVeuillez trouver ci-joint le template "${template.nom}".\n\nCordialement`);
    setShowShareDialog(true);
  };

  // Envoyer le partage par email (via backend)
  const handleSendShare = async () => {
    if (!shareEmail) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/coopers/templates/${shareTemplate.id}/share`,
        {
          cooper_id: userId,
          email: shareEmail,
          message: shareMessage
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(`Template partagé avec ${shareEmail}`);
      setShowShareDialog(false);
    } catch {
      // Fallback: copy share link to clipboard
      const shareLink = `${window.location.origin}/templates/cooper/${shareTemplate.id}`;
      navigator.clipboard.writeText(shareLink);
      toast.info('Lien copié dans le presse-papiers (email non disponible)');
      setShowShareDialog(false);
    }
  };

  // Copier le lien de partage
  const handleCopyShareLink = () => {
    const shareLink = `${window.location.origin}/templates/cooper/${shareTemplate.id}`;
    navigator.clipboard.writeText(shareLink);
    toast.success('Lien copié dans le presse-papiers');
  };

  // Ouvrir le dialogue d'édition
  const openEditDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      const standardTypes = ['proposition', 'conditions', 'attestation'];
      const isStandard = standardTypes.includes(template.type_document);
      
      setIsCustomType(!isStandard);
      setCustomTypeValue(isStandard ? '' : template.type_document);
      setFormData({
        nom: template.nom,
        type_document: isStandard ? template.type_document : 'custom',
        contenu_html: template.contenu_html,
        description: template.description
      });
    } else {
      setEditingTemplate(null);
      setIsCustomType(false);
      setCustomTypeValue('');
      setFormData({ nom: '', type_document: 'proposition', contenu_html: '', description: '' });
    }
    setIsEditing(true);
  };


  return { userId, getAuthHeader, templates, setTemplates, variablesDisponibles, setVariablesDisponibles, loading, setLoading, error, setError, success, setSuccess, isEditing, setIsEditing, editingTemplate, setEditingTemplate, formData, setFormData, isCustomType, setIsCustomType, customTypeValue, setCustomTypeValue, showPreview, setShowPreview, previewTemplate, setPreviewTemplate, variablesExpanded, setVariablesExpanded, showShareDialog, setShowShareDialog, shareTemplate, setShareTemplate, shareEmail, setShareEmail, shareMessage, setShareMessage, editorRef, fetchingRef, insertVariable, applyFormat, insertHeading, handleGenerateDocument, fetchTemplates, handleSaveTemplate, handleDeleteTemplate, handleDuplicateTemplate, handleLoadDefaultTemplate, toggleTemplateActive, handlePreviewTemplate, generateFullHTML, handlePrintTemplate, handleDownloadPDF, handleShareTemplate, handleSendShare, handleCopyShareLink, openEditDialog };
}
